import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, FileText } from "lucide-react";
import { api } from "../lib/api";
import { useLang, whatsappLink } from "../i18n";
import { PRODUCTS, findProduct } from "../data/products";
import { Reveal, Eyebrow, MaskedLine } from "../components/Reveal";
import { ParallaxImage } from "../components/ParallaxImage";
import EditorialBreak from "../components/EditorialBreak";
import EditorialProject from "../components/EditorialProject";
import { WhatsAppIcon } from "../components/WhatsAppButton";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const product = findProduct(slug);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    if (!product) return;
    api.get("/projects", { params: { category: product.category } }).then(({ data }) => setRelated(data.slice(0, 2))).catch(() => {});
  }, [product]);

  if (!product) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="product-not-found">
        <p className="font-serif text-3xl text-[#F3EFE6]">404</p>
        <Link to="/prodotti" className="mt-4 inline-block text-sm uppercase tracking-[0.15em] text-[#C8A86B]">{t.nav_products}</Link>
      </div>
    );
  }

  const L = (k) => product[`${k}_${lang}`];
  const name = L("name");
  const specs = L("specs");
  const benefits = L("benefits");
  const idx = PRODUCTS.filter((p) => !p.href).findIndex((p) => p.slug === slug);
  const nextP = PRODUCTS.filter((p) => !p.href)[(idx + 1) % PRODUCTS.filter((p) => !p.href).length];
  const [ed1, ed2] = product.editorial || [];
  const E = (e, k) => e[`${k}_${lang}`];

  return (
    <div data-testid="product-detail-page">
      {/* 1. FULL-SCREEN IMAGE */}
      <section className="relative isolate h-[100svh] min-h-[600px] flex flex-col justify-end overflow-hidden">
        <ParallaxImage src={product.image} alt={name} className="absolute inset-0 -z-10" strength={10} loading="eager" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#080808] via-[#080808]/40 to-[#080808]/20" />
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
          <Link to="/prodotti" data-testid="back-to-products" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A39E93] transition-colors hover:text-[#C8A86B]">
            <ArrowLeft size={14} /> {t.nav_products}
          </Link>
          <p className="mt-10 font-mono text-xs tracking-[0.3em] text-[#C8A86B]">{product.number}</p>
          <h1 className="mt-4 font-serif font-light tracking-tight leading-[0.98] text-[#F3EFE6] text-5xl sm:text-6xl lg:text-8xl">
            <MaskedLine delay={0.2}>{name}</MaskedLine>
          </h1>
        </div>
      </section>

      {/* 2. LARGE HEADLINE + 3. SHORT EMOTIONAL SENTENCE */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-44">
        <Reveal>
          <p data-testid="product-emotion" className="max-w-5xl font-serif italic font-light tracking-tight leading-[1.05] text-[#C8A86B] text-4xl sm:text-6xl lg:text-7xl">{L("emotion")}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-10 max-w-md text-base sm:text-lg font-light leading-relaxed text-[#A39E93]">{L("tagline")}</p>
        </Reveal>
      </section>

      {/* 4. FULL-WIDTH IMAGE */}
      {ed1 && <EditorialBreak testid="product-break-1" image={ed1.image} headline={E(ed1, "headline")} line={E(ed1, "line")} position={ed1.position} />}

      {/* 5. ELEGANT TEXT BLOCK */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-44">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-4"><Eyebrow>{t.product_intro_eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1} className="lg:col-span-8">
            <p data-testid="product-intro" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.3] text-[#F3EFE6]">{L("intro")}</p>
          </Reveal>
        </div>
        <div className="mt-24 sm:mt-32 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-4"><Eyebrow>{t.product_benefits_eyebrow}</Eyebrow></Reveal>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-12">
            {benefits.map(([title, text], i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="border-t border-[#1C1A17] py-8 sm:py-10">
                  <span className="font-mono text-xs tracking-[0.3em] text-[#C8A86B]">0{i + 1}</span>
                  <h3 className="mt-5 font-serif text-2xl sm:text-3xl font-light text-[#F3EFE6]">{title}</h3>
                  <p className="mt-4 text-sm sm:text-base font-light leading-relaxed text-[#A39E93]">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. ANOTHER LARGE IMAGE */}
      {ed2 && <EditorialBreak testid="product-break-2" image={ed2.image} headline={E(ed2, "headline")} line={E(ed2, "line")} align="right" />}

      {/* SOLUTIONS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
        <Reveal><Eyebrow>{t.product_variants_eyebrow}</Eyebrow></Reveal>
        <div className="mt-12">
          {L("variants").map(([title, text], i) => (
            <Reveal key={title} delay={i * 0.05}>
              <div data-testid={`variant-${i}`} className="group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 border-t border-[#1C1A17] py-10 transition-colors duration-500 hover:bg-[#0F0F0E]">
                <span className="md:col-span-1 font-mono text-2xl text-[#C8A86B]/60 group-hover:text-[#C8A86B] transition-colors">{i + 1}</span>
                <h3 className="md:col-span-4 font-serif text-2xl sm:text-3xl font-light text-[#F3EFE6]">{title}</h3>
                <p className="md:col-span-7 text-sm sm:text-base font-light leading-relaxed text-[#A39E93]">{text}</p>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-[#1C1A17]" />
        </div>
      </section>

      {/* SPECS + DATASHEET PLACEHOLDER */}
      <section className="border-t border-[#1C1A17] bg-[#0A0A09]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-7">
            <div className="card-float border border-[#262420] bg-[#0F0F0E] p-7 sm:p-10">
              <Eyebrow>{t.product_specs_eyebrow}</Eyebrow>
              <dl data-testid="product-specs" className="mt-6 flex flex-col">
                {Object.entries(specs).map(([k, v]) => (
                  <div key={k} className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4 border-t border-[#1C1A17] py-4">
                    <dt className="text-xs font-mono uppercase tracking-wider text-[#827D72]">{k}</dt>
                    <dd className="sm:text-right text-sm text-[#F3EFE6]">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 text-xs font-light text-[#666157]">{t.product_specs_note}</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div data-testid="product-datasheet-placeholder" className="card-float flex h-full flex-col justify-between border border-dashed border-[#C8A86B]/30 bg-[#080808] p-7 sm:p-10">
              <div>
                <FileText size={28} className="text-[#C8A86B]" />
                <h3 className="mt-6 font-serif text-2xl text-[#F3EFE6]">{t.product_datasheet_title}</h3>
                <p className="mt-3 text-sm font-light leading-relaxed text-[#A39E93]">{t.product_datasheet_sub}</p>
              </div>
              <a href={whatsappLink(`Buongiorno, vorrei ricevere la scheda tecnica di: ${name}.`)} target="_blank" rel="noopener noreferrer" data-testid="product-datasheet-request" className="btn-lift mt-8 inline-flex w-fit items-center gap-2 border border-[#C8A86B] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#C8A86B] hover:bg-[#C8A86B] hover:text-[#080808]">
                <WhatsAppIcon size={14} /> {t.downloads_placeholder_cta}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELATED — editorial */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
          <Reveal><Eyebrow>{t.product_related}</Eyebrow></Reveal>
          <div className="mt-16 flex flex-col gap-24">
            {related.map((p, i) => (
              <Reveal key={p.id} delay={0.05}><EditorialProject project={p} index={i} /></Reveal>
            ))}
          </div>
        </section>
      )}

      {/* CTA + NEXT */}
      <section className="relative overflow-hidden border-t border-[#1C1A17]">
        <div className="absolute inset-0 bronze-glow" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40">
          <Reveal>
            <h2 className="max-w-3xl font-serif text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F3EFE6]">{t.product_cta} <span className="italic text-[#C8A86B]">{name}</span></h2>
            <a href={whatsappLink(`Buongiorno, vorrei un preventivo per: ${name}.`)} target="_blank" rel="noopener noreferrer" data-testid="product-quote-cta" className="mt-10 inline-flex items-center gap-3 bg-[#C8A86B] px-8 py-4 text-sm uppercase tracking-[0.15em] text-[#080808] hover:bg-[#D8BD85]">
              <WhatsAppIcon size={16} /> {t.hero_cta_contact}
            </a>
          </Reveal>
          <Reveal className="mt-24">
            <div className="hairline" />
            <Link to={`/prodotti/${nextP.slug}`} data-testid="next-product-link" className="group mt-10 flex items-center justify-between gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] font-mono text-[#827D72]">{nextP.number}</p>
                <p className="mt-3 font-serif text-3xl sm:text-4xl font-light text-[#F3EFE6] transition-colors group-hover:text-[#D8BD85]">{lang === "it" ? nextP.name_it : nextP.name_en}</p>
              </div>
              <ArrowRight size={28} className="shrink-0 text-[#C8A86B] transition-transform duration-300 group-hover:translate-x-2" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
